import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Switch,
} from '@roger/ui-components';
import { Loader2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';

import {
  getAgencyConfig,
  updateAgencyConfig,
} from '../../services/agencyConfig';
import { useStore } from '../../store';

interface RecurringEligibilityDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The agency's opt-in for the weekly roster eligibility sweep.
 *
 * Super users only: each check can be billable, so Roger decides whether an
 * agency gets it. The API refuses everyone else.
 */
export function RecurringEligibilityDialog({
  open,
  onOpenChange,
}: RecurringEligibilityDialogProps) {
  const { showSuccess, showError } = useStore(
    useShallow(state => ({
      showSuccess: state.showSuccess,
      showError: state.showError,
    }))
  );

  const [enabled, setEnabled] = useState(false);
  const [savedEnabled, setSavedEnabled] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setIsLoading(true);
    setLoadFailed(false);
    getAgencyConfig()
      .then(cfg => {
        if (cancelled) return;
        const value = Boolean(cfg.recurring_eligibility_checks_enabled);
        setEnabled(value);
        setSavedEnabled(value);
      })
      .catch(err => {
        console.error('Failed to load agency config:', err);
        if (!cancelled) setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const handleSave = async () => {
    if (enabled === savedEnabled) {
      onOpenChange(false);
      return;
    }
    setIsSaving(true);
    try {
      await updateAgencyConfig({
        recurring_eligibility_checks_enabled: enabled,
      });
      showSuccess('Recurring eligibility setting updated');
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save recurring eligibility setting:', err);
      showError('Failed to update recurring eligibility setting');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[480px]'>
        <DialogHeader>
          <DialogTitle>Recurring eligibility checks</DialogTitle>
          <DialogDescription>
            Every Monday at 5am Pacific, re-check primary insurance for patients
            on the active census, skipping any checked in the last 7 days.
            Catches lapsed coverage before billing. Each check may be billable
            on some plans.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2 className='h-4 w-4 animate-spin' />
            Loading…
          </div>
        ) : loadFailed ? (
          <p className='text-sm text-destructive'>
            Could not load this setting. Close and try again.
          </p>
        ) : (
          <label className='flex items-center gap-3 text-sm'>
            <Switch
              checked={enabled}
              onCheckedChange={setEnabled}
              disabled={isSaving}
              aria-label='Recurring eligibility checks'
            />
            {enabled ? 'On' : 'Off'}
          </label>
        )}

        <DialogFooter>
          <Button variant='outline' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSave()}
            disabled={isLoading || loadFailed || isSaving}
          >
            {isSaving && <Loader2 className='mr-2 h-4 w-4 animate-spin' />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
